import { useMemo, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { PackageItemStatus } from '@pirouette/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/Empty';
import { useDancers } from '@/data/dancers';
import { useProductions } from '@/data/productions';
import {
  packageItemStatusOptions,
  useAssignRecitalPackage,
  useCreateRecitalPackage,
  useCreateRecitalPackageCharge,
  useDancerRecitalPackages,
  useRecitalPackages,
  useUpdateDancerRecitalPackageStatus,
} from '@/data/recitalPackages';

export const Route = createFileRoute('/admin/recital-packages/')({
  component: RecitalPackagesIndex,
});

const statusLabels: Record<PackageItemStatus, string> = {
  not_started: 'Not started',
  ordered: 'Ordered',
  received: 'Received',
  delivered: 'Delivered',
  waived: 'Waived',
};

function dancerName(dancer: {
  first_name: string;
  last_name: string;
  preferred_name?: string | null | undefined;
}) {
  return `${dancer.preferred_name ?? dancer.first_name} ${dancer.last_name}`;
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatSqlDate(value: string | null) {
  if (!value) return 'No due date';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

function RecitalPackagesIndex() {
  const productions = useProductions();
  const dancers = useDancers({ status: 'active' });
  const [selectedProductionId, setSelectedProductionId] = useState('');
  const packages = useRecitalPackages(selectedProductionId || undefined);
  const assignments = useDancerRecitalPackages();
  const createPackage = useCreateRecitalPackage();
  const assignPackage = useAssignRecitalPackage();
  const updateStatus = useUpdateDancerRecitalPackageStatus();
  const createCharge = useCreateRecitalPackageCharge();

  const [packageName, setPackageName] = useState('Recital Package');
  const [price, setPrice] = useState('165.00');
  const [dueDate, setDueDate] = useState('');
  const [includesTshirt, setIncludesTshirt] = useState(true);
  const [includedTickets, setIncludedTickets] = useState('2');
  const [includesVideo, setIncludesVideo] = useState(true);
  const [includesProgram, setIncludesProgram] = useState(true);
  const [assignDancerId, setAssignDancerId] = useState('');
  const [assignPackageId, setAssignPackageId] = useState('');

  const packageMap = useMemo(
    () => new Map((packages.data ?? []).map((pkg) => [pkg.id, pkg])),
    [packages.data],
  );
  const assignablePackages = useMemo(
    () => (packages.data ?? []).filter((pkg) => pkg.active),
    [packages.data],
  );

  const visibleAssignments = useMemo(() => {
    if (!selectedProductionId) return assignments.data ?? [];
    return (assignments.data ?? []).filter(
      (assignment) => assignment.recital_packages?.production_id === selectedProductionId,
    );
  }, [assignments.data, selectedProductionId]);

  async function onCreatePackage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedProductionId) return;
    await createPackage.mutateAsync({
      production_id: selectedProductionId,
      name: packageName,
      price_cents: Math.round(Number(price) * 100),
      includes_costume: true,
      includes_tshirt: includesTshirt,
      included_ticket_count: Number(includedTickets),
      includes_video: includesVideo,
      includes_program: includesProgram,
      due_date: dueDate || undefined,
      active: true,
    });
    setPackageName('Recital Package');
    setPrice('165.00');
    setDueDate('');
  }

  async function onAssignPackage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!assignDancerId || !assignPackageId) return;
    await assignPackage.mutateAsync({
      dancer_id: assignDancerId,
      package_id: assignPackageId,
    });
    setAssignDancerId('');
    setAssignPackageId('');
  }

  async function onStatusChange(
    id: string,
    field: 'costume_status' | 'tshirt_status' | 'ticket_status' | 'video_status' | 'program_status',
    event: ChangeEvent<HTMLSelectElement>,
  ) {
    await updateStatus.mutateAsync({
      id,
      [field]: event.target.value as PackageItemStatus,
    });
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Recital operations
        </p>
        <h1 className="mt-2">Recital Packages</h1>
      </header>

      <section aria-labelledby="filters-heading" className="surface p-5">
        <h2 id="filters-heading" className="text-[var(--text-xl)]">
          Production filter
        </h2>
        <label className="mt-4 grid max-w-xl gap-2">
          <span className="font-medium text-[var(--text-sm)]">Production</span>
          <select
            className="h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
            value={selectedProductionId}
            onChange={(event) => {
              setSelectedProductionId(event.target.value);
              setAssignPackageId('');
            }}
          >
            <option value="">All productions</option>
            {(productions.data ?? []).map((production) => (
              <option key={production.id} value={production.id}>
                {production.name} · {production.season}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section aria-labelledby="create-package-heading" className="surface p-5">
        <h2 id="create-package-heading" className="text-[var(--text-xl)]">
          Package definition
        </h2>
        <form onSubmit={(event) => void onCreatePackage(event)} className="mt-5 grid gap-4">
          <div className="grid gap-4 md:grid-cols-[1.5fr_0.6fr_0.7fr]">
            <label className="grid gap-2">
              <span className="font-medium text-[var(--text-sm)]">Name</span>
              <input
                className="h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                value={packageName}
                onChange={(event) => setPackageName(event.target.value)}
                required
              />
            </label>
            <label className="grid gap-2">
              <span className="font-medium text-[var(--text-sm)]">Price</span>
              <input
                className="h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                inputMode="decimal"
                required
              />
            </label>
            <label className="grid gap-2">
              <span className="font-medium text-[var(--text-sm)]">Due date</span>
              <input
                className="h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includesTshirt}
                onChange={(event) => setIncludesTshirt(event.target.checked)}
              />
              <span>T-shirt</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includesVideo}
                onChange={(event) => setIncludesVideo(event.target.checked)}
              />
              <span>Video</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includesProgram}
                onChange={(event) => setIncludesProgram(event.target.checked)}
              />
              <span>Program</span>
            </label>
            <label className="flex items-center gap-2">
              <span>Tickets</span>
              <input
                className="h-9 w-20 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                value={includedTickets}
                onChange={(event) => setIncludedTickets(event.target.value)}
                inputMode="numeric"
              />
            </label>
          </div>

          <div>
            <Button type="submit" disabled={!selectedProductionId || createPackage.isPending}>
              {createPackage.isPending ? 'Creating...' : 'Create package'}
            </Button>
          </div>
        </form>
      </section>

      <section aria-labelledby="packages-heading" className="space-y-4">
        <h2 id="packages-heading" className="text-[var(--text-xl)]">
          Packages
        </h2>
        {packages.isLoading && <p aria-live="polite">Loading...</p>}
        {(packages.data ?? []).length === 0 && !packages.isLoading ? (
          <Empty
            title="No recital packages"
            description="Create a package after choosing a production."
          />
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {(packages.data ?? []).map((pkg) => (
              <li key={pkg.id} className="surface-elevated p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-[var(--font-display)] text-[var(--text-xl)]">{pkg.name}</h3>
                    <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      {pkg.productions?.name ?? 'Production'} · {formatSqlDate(pkg.due_date)}
                    </p>
                  </div>
                  <Badge tone={pkg.active ? 'success' : 'neutral'}>
                    {pkg.active ? 'active' : 'inactive'}
                  </Badge>
                </div>
                <p className="mt-3 font-medium">{formatMoney(pkg.price_cents)}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {pkg.includes_costume && <Badge>costume</Badge>}
                  {pkg.includes_tshirt && <Badge>t-shirt</Badge>}
                  {pkg.included_ticket_count > 0 && (
                    <Badge>{pkg.included_ticket_count} tickets</Badge>
                  )}
                  {pkg.includes_video && <Badge>video</Badge>}
                  {pkg.includes_program && <Badge>program</Badge>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="assign-heading" className="surface p-5">
        <h2 id="assign-heading" className="text-[var(--text-xl)]">
          Assign package
        </h2>
        <form
          onSubmit={(event) => void onAssignPackage(event)}
          className="mt-5 grid gap-4 md:grid-cols-[1fr_1fr_auto]"
        >
          <label className="grid gap-2">
            <span className="font-medium text-[var(--text-sm)]">Dancer</span>
            <select
              className="h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              value={assignDancerId}
              onChange={(event) => setAssignDancerId(event.target.value)}
              required
            >
              <option value="">Choose dancer</option>
              {(dancers.data?.rows ?? []).map((dancer) => (
                <option key={dancer.id} value={dancer.id}>
                  {dancerName(dancer)}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-2">
            <span className="font-medium text-[var(--text-sm)]">Package</span>
            <select
              className="h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              value={assignPackageId}
              onChange={(event) => setAssignPackageId(event.target.value)}
              required
            >
              <option value="">Choose package</option>
              {assignablePackages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" className="self-end" disabled={assignPackage.isPending}>
            {assignPackage.isPending ? 'Assigning...' : 'Assign'}
          </Button>
        </form>
      </section>

      <section aria-labelledby="fulfillment-heading" className="space-y-4">
        <h2 id="fulfillment-heading" className="text-[var(--text-xl)]">
          Fulfillment
        </h2>
        {assignments.isLoading && <p aria-live="polite">Loading...</p>}
        {visibleAssignments.length === 0 && !assignments.isLoading ? (
          <Empty
            title="No package assignments"
            description="Assigned dancers will appear here for fulfillment tracking."
          />
        ) : (
          <div className="surface overflow-x-auto">
            <table className="min-w-full text-left text-[var(--text-sm)]">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="px-4 py-3">Dancer</th>
                  <th className="px-4 py-3">Package</th>
                  <th className="px-4 py-3">Costume</th>
                  <th className="px-4 py-3">T-shirt</th>
                  <th className="px-4 py-3">Tickets</th>
                  <th className="px-4 py-3">Video</th>
                  <th className="px-4 py-3">Program</th>
                  <th className="px-4 py-3">Charge</th>
                </tr>
              </thead>
              <tbody>
                {visibleAssignments.map((assignment) => {
                  const pkg = assignment.recital_packages ?? packageMap.get(assignment.package_id);
                  return (
                    <tr key={assignment.id} className="border-b border-[var(--color-border)]">
                      <td className="px-4 py-3">
                        {assignment.dancers ? dancerName(assignment.dancers) : 'Dancer'}
                      </td>
                      <td className="px-4 py-3">{pkg?.name ?? 'Package'}</td>
                      {(
                        [
                          ['costume_status', assignment.costume_status],
                          ['tshirt_status', assignment.tshirt_status],
                          ['ticket_status', assignment.ticket_status],
                          ['video_status', assignment.video_status],
                          ['program_status', assignment.program_status],
                        ] as const
                      ).map(([field, value]) => (
                        <td key={field} className="px-4 py-3">
                          <select
                            className="h-9 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-2"
                            value={value}
                            onChange={(event) => void onStatusChange(assignment.id, field, event)}
                          >
                            {packageItemStatusOptions.map((status) => (
                              <option key={status} value={status}>
                                {statusLabels[status]}
                              </option>
                            ))}
                          </select>
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        {assignment.charge_id ? (
                          <Badge tone="success">created</Badge>
                        ) : (
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => void createCharge.mutateAsync(assignment.id)}
                            disabled={createCharge.isPending}
                          >
                            Create charge
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
